import React from 'react';
import SectionCard from './SectionCard';
import { buildStrengthNarrative, formatPercent, plural } from '../utils/narrative';

const STRONG_LEVELS = ['mastered', 'proficient'];

function isStrong(level) {
  return STRONG_LEVELS.includes(String(level || '').toLowerCase());
}

// Spec 1d puts a ranked strength breakdown in the left column. The only ranked
// per-topic scores the API returns are the adaptive mastery scores; when those
// aren't enabled we fall back to the lessons whose quizzes have been passed,
// which is the only other honest read on "what you're strong at".
export default function StrengthsCard({ data }) {
  const masteries = (data?.adaptiveV2Enabled ? data?.v2TopicMasteries : null) || [];
  const scored = masteries
    .filter((topic) => topic && topic.masteryScore != null)
    .sort((a, b) => b.masteryScore - a.masteryScore);

  if (scored.length > 0) {
    const shown = scored.slice(0, 6);
    return (
      <SectionCard
        title="Strengths by topic"
        meta={`${scored.length} ${plural(scored.length, 'topic')} tracked`}
        lede={buildStrengthNarrative(scored) || 'Mastery updates every time you answer a question on a topic.'}
        footer="Solid bars are topics you're already proficient in; muted bars are still developing."
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {shown.map((topic) => {
            const percent = Math.round(topic.masteryScore * 100);
            return (
              <div key={topic.topicId}>
                <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, marginBottom: 6 }}>
                  <span
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 7,
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--text-primary)',
                      minWidth: 0,
                    }}
                  >
                    <span
                      style={{
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {topic.topicName || 'Untitled topic'}
                    </span>
                    {topic.isReliable === false && (
                      <span
                        style={{
                          flex: 'none',
                          fontSize: 10,
                          fontWeight: 500,
                          letterSpacing: '0.04em',
                          textTransform: 'uppercase',
                          color: 'var(--warning)',
                          background: 'var(--warning-bg)',
                          border: '1px solid var(--border-subtle)',
                          borderRadius: 4,
                          padding: '1px 5px',
                        }}
                      >
                        Calibrating
                      </span>
                    )}
                  </span>
                  <span
                    style={{
                      fontSize: 13,
                      fontWeight: 500,
                      color: 'var(--text-secondary)',
                      fontVariantNumeric: 'tabular-nums',
                    }}
                  >
                    {percent}%
                  </span>
                </div>
                <div className="ins-meter-track">
                  <div
                    className="ins-meter-fill"
                    style={{
                      width: `${percent}%`,
                      background: isStrong(topic.masteryLevel) ? 'var(--accent-primary)' : 'var(--text-tertiary)',
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </SectionCard>
    );
  }

  const mastered = Array.isArray(data?.topicsMastered) ? data.topicsMastered : [];
  const strongest = data?.strongestTopic;
  if (mastered.length === 0 && !strongest) return null;

  const total = data?.topicsMasteredCount ?? mastered.length;
  return (
    <SectionCard
      title="What you've mastered"
      meta={total > 0 ? `${total} ${plural(total, 'lesson')} passed` : undefined}
      lede={
        strongest
          ? `Most of your passed quizzes sit in ${strongest.tag} — ${strongest.quizCount} ${plural(strongest.quizCount, 'quiz', 'quizzes')} so far.`
          : 'These are the lessons whose quizzes you have passed.'
      }
      footer={
        data?.quizPassRate != null
          ? `You pass ${formatPercent(data.quizPassRate)} of the quizzes you attempt.`
          : undefined
      }
    >
      {mastered.length > 0 ? (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
          {mastered.slice(0, 8).map((title, i) => (
            <span
              key={`${title}-${i}`}
              style={{
                fontSize: 12.5,
                padding: '5px 10px',
                background: 'var(--bg-tertiary)',
                borderRadius: 'var(--radius-sm)',
                color: 'var(--text-primary)',
              }}
            >
              {title}
            </span>
          ))}
          {total > 8 && (
            <span style={{ alignSelf: 'center', fontSize: 12.5, color: 'var(--text-tertiary)' }}>
              +{total - 8} more
            </span>
          )}
        </div>
      ) : (
        <p style={{ margin: 0, fontSize: 13, color: 'var(--text-tertiary)' }}>
          Pass a lesson quiz and it shows up here.
        </p>
      )}
    </SectionCard>
  );
}
